import { Controller } from "@hotwired/stimulus";
import { barHeights, barsPath, serverPeaks } from "../lib/peaks.js";
import {
  END_RESET_S,
  LOAD_TIMEOUT_MS,
  SEEK_SETTLE_TOLERANCE_S,
  SEEK_TIMEOUT_MS,
  SKIP_SECONDS,
  barCount,
  clampPosition,
  offsetToPosition,
} from "../lib/scrub.js";
import { formatDuration, formatRemaining } from "../lib/time.js";

const HEIGHT = 48;

// Only one player plays at a time on a page.
let current = null;

// Only one card is expanded at a time on a page.
let expandedCard = null;

// One recording's player: a custom waveform scrubber over a single
// HTMLAudioElement. The controller sits on the card's root element. A card
// either expands in place (a header toggle plus a `panel` that animates open,
// one card at a time) or swaps a metadata view with a player view.
export default class extends Controller {
  static targets = [
    "basePath",
    "elapsed",
    "message",
    "metadataView",
    "panel",
    "pauseIcon",
    "playButton",
    "playIcon",
    "played",
    "playedPath",
    "playerView",
    "playhead",
    "remaining",
    "skipBack",
    "skipForward",
    "slider",
    "spinnerIcon",
    "toggle",
  ];

  static values = {
    duration: Number,
    peaks: Array,
    seed: String,
    url: String,
  };

  connect() {
    this.audio = null;
    this.failed = false;
    this.loading = false;
    this.playing = false;
    this.scrubTime = null;
    this.seekingTo = null;
    this.frame = null;
    this.width = 0;
    this.lastWhole = -1;
    this.lastBars = -1;
    this.expanded = this.hasPanelTarget && this.panelTarget.hasAttribute("data-open");

    this.onKeydown = this.keydown.bind(this);
    this.element.addEventListener("keydown", this.onKeydown);

    if (this.hasSliderTarget) {
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.sliderTarget);
    }
    if (this.playerVisible) this.ensureAudio();
    this.measure();
    this.render();
  }

  disconnect() {
    this.element.removeEventListener("keydown", this.onKeydown);
    this.resizeObserver?.disconnect();
    this.stopFrames();
    clearTimeout(this.loadTimer);
    clearTimeout(this.seekTimer);
    if (current === this) current = null;
    if (expandedCard === this) expandedCard = null;
    if (this.audio) {
      this.audio.pause();
      this.audio.removeAttribute("src");
      this.audio.load();
      this.audio = null;
    }
  }

  // --- view switching -------------------------------------------------------

  open() {
    if (this.hasMetadataViewTarget) this.metadataViewTarget.classList.add("hidden");
    if (this.hasPlayerViewTarget) this.playerViewTarget.classList.remove("hidden");
    this.ensureAudio();
    this.measure();
    this.render();
  }

  close() {
    this.pause();
    if (this.hasPlayerViewTarget) this.playerViewTarget.classList.add("hidden");
    if (this.hasMetadataViewTarget) this.metadataViewTarget.classList.remove("hidden");
  }

  // --- expanding (cards) ----------------------------------------------------

  toggleExpanded() {
    if (this.expanded) this.collapse();
    else this.expand();
  }

  expand() {
    if (!this.hasPanelTarget || this.expanded) return;
    if (expandedCard && expandedCard !== this) expandedCard.collapse();
    expandedCard = this;
    this.expanded = true;
    this.applyExpanded();
    this.ensureAudio();
    this.measure();
    this.render();
  }

  collapse() {
    if (!this.hasPanelTarget || !this.expanded) return;
    this.pause();
    this.expanded = false;
    if (expandedCard === this) expandedCard = null;
    this.applyExpanded();
  }

  applyExpanded() {
    this.panelTarget.toggleAttribute("data-open", this.expanded);
    this.panelTarget.inert = !this.expanded;
    if (this.hasToggleTarget) {
      this.toggleTarget.setAttribute("aria-expanded", String(this.expanded));
    }
  }

  get playerVisible() {
    if (this.hasPanelTarget) return this.expanded;
    return (
      this.hasPlayerViewTarget &&
      !this.playerViewTarget.classList.contains("hidden")
    );
  }

  // --- audio ----------------------------------------------------------------

  ensureAudio() {
    if (this.audio || !this.urlValue || !this.hasSliderTarget) return;
    const audio = new Audio();
    audio.preload = "metadata";
    audio.addEventListener("loadedmetadata", () => this.render());
    audio.addEventListener("durationchange", () => this.render());
    audio.addEventListener("seeked", () => this.render());
    audio.addEventListener("timeupdate", () => {
      if (!this.playing) this.render();
    });
    audio.addEventListener("canplay", () => this.loaded());
    audio.addEventListener("playing", () => this.loaded());
    audio.addEventListener("waiting", () => {
      if (this.playing) this.setLoading(true);
    });
    audio.addEventListener("play", () => this.setPlaying(true));
    audio.addEventListener("pause", () => this.setPlaying(false));
    audio.addEventListener("ended", () => this.ended());
    audio.addEventListener("error", () => this.fail());
    audio.src = this.urlValue;
    this.audio = audio;
  }

  get duration() {
    const d = this.audio?.duration;
    return Number.isFinite(d) && d > 0 ? d : this.durationValue || 0;
  }

  // The position to show: the drag, else the seek target until the audio
  // reports a position near it, else the audio's own position.
  get position() {
    if (this.scrubTime !== null) return this.scrubTime;
    const actual = this.audio?.currentTime || 0;
    if (this.seekingTo !== null) {
      if (Math.abs(actual - this.seekingTo) > SEEK_SETTLE_TOLERANCE_S) {
        return this.seekingTo;
      }
      this.clearSeeking();
    }
    return actual;
  }

  toggle() {
    if (this.playing) this.pause();
    else this.play();
  }

  async play() {
    this.ensureAudio();
    if (!this.audio || this.failed) return;

    if (current && current !== this) current.pause();
    current = this;

    const d = this.duration;
    if (d > 0 && this.audio.currentTime >= d - END_RESET_S) this.moveTo(0);

    if (this.audio.readyState < 3) {
      this.setLoading(true);
      clearTimeout(this.loadTimer);
      this.loadTimer = setTimeout(() => this.fail(), LOAD_TIMEOUT_MS);
    }

    try {
      await this.audio.play();
    } catch (error) {
      // A pause (or another player starting) interrupts play(); a blocked
      // autoplay is not a broken recording either.
      if (error.name === "AbortError" || error.name === "NotAllowedError") {
        this.loaded();
      } else {
        this.fail();
      }
    }
  }

  pause() {
    this.audio?.pause();
    this.loaded();
  }

  loaded() {
    clearTimeout(this.loadTimer);
    this.setLoading(false);
  }

  ended() {
    this.setPlaying(false);
    this.clearSeeking();
    if (this.audio) this.audio.currentTime = 0;
    this.render();
  }

  fail() {
    if (this.failed) return;
    this.failed = true;
    clearTimeout(this.loadTimer);
    this.audio?.pause();
    this.loading = false;
    this.setPlaying(false);
    if (this.hasMessageTarget) this.messageTarget.classList.remove("hidden");
    if (this.hasSliderTarget) {
      this.sliderTarget.setAttribute("aria-disabled", "true");
      this.sliderTarget.tabIndex = -1;
    }
    this.renderButtons();
  }

  setPlaying(playing) {
    this.playing = playing;
    if (playing) {
      if (this.frame === null) this.frame = requestAnimationFrame(() => this.tick());
    } else {
      this.stopFrames();
      if (current === this) current = null;
    }
    this.renderButtons();
    this.render();
  }

  setLoading(loading) {
    this.loading = loading;
    this.renderButtons();
  }

  tick() {
    this.frame = null;
    if (!this.playing) return;
    this.render();
    this.frame = requestAnimationFrame(() => this.tick());
  }

  stopFrames() {
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  // --- seeking --------------------------------------------------------------

  // Moves the audio to `seconds` (clamped) and holds the display there until
  // the audio catches up.
  moveTo(seconds) {
    this.ensureAudio();
    if (!this.audio || this.failed) return;
    const target = clampPosition(seconds, this.duration);
    this.clearSeeking();
    this.seekingTo = target;
    this.seekTimer = setTimeout(() => this.clearSeeking(), SEEK_TIMEOUT_MS);
    try {
      this.audio.currentTime = target;
    } catch (_error) {
      this.clearSeeking();
    }
    this.render();
  }

  clearSeeking() {
    clearTimeout(this.seekTimer);
    this.seekTimer = null;
    this.seekingTo = null;
  }

  skipBack() {
    this.moveTo(this.position - SKIP_SECONDS);
  }

  skipForward() {
    this.moveTo(this.position + SKIP_SECONDS);
  }

  // --- scrubbing (Pointer Events: mouse and touch) ---------------------------

  get scrubbable() {
    return !this.failed && this.duration > 0 && this.width > 0;
  }

  timeAt(event) {
    const rect = this.sliderTarget.getBoundingClientRect();
    return offsetToPosition(event.clientX - rect.left, rect.width, this.duration);
  }

  scrubStart(event) {
    if (event.button > 0 || !this.scrubbable) return;
    this.ensureAudio();
    this.sliderTarget.setPointerCapture(event.pointerId);
    this.sliderTarget.toggleAttribute("data-scrubbing", true);
    this.scrubTime = this.timeAt(event);
    this.render();
  }

  scrubMove(event) {
    if (this.scrubTime === null) return;
    this.scrubTime = this.timeAt(event);
    this.render();
  }

  scrubEnd(event) {
    if (this.scrubTime === null) return;
    const target = this.timeAt(event);
    this.scrubTime = null;
    this.releasePointer(event);
    this.moveTo(target);
  }

  scrubCancel(event) {
    if (this.scrubTime === null) return;
    this.scrubTime = null;
    this.releasePointer(event);
    this.render();
  }

  releasePointer(event) {
    this.sliderTarget.toggleAttribute("data-scrubbing", false);
    if (this.sliderTarget.hasPointerCapture?.(event.pointerId)) {
      this.sliderTarget.releasePointerCapture(event.pointerId);
    }
  }

  // --- keyboard -------------------------------------------------------------

  sliderKeydown(event) {
    if (event.altKey || event.ctrlKey || event.metaKey || !this.scrubbable) return;
    let target;
    switch (event.key) {
      case "ArrowLeft":
      case "ArrowDown":
        target = this.position - SKIP_SECONDS;
        break;
      case "ArrowRight":
      case "ArrowUp":
        target = this.position + SKIP_SECONDS;
        break;
      case "Home":
        target = 0;
        break;
      case "End":
        target = this.duration;
        break;
      default:
        return;
    }
    event.preventDefault();
    this.moveTo(target);
  }

  // Space or K toggles play while focus is inside the player.
  keydown(event) {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
    const target = event.target;
    if (event.key === "Escape" && this.expanded) {
      event.preventDefault();
      this.collapse();
      if (this.hasToggleTarget) this.toggleTarget.focus();
      return;
    }
    // Space and K only play from inside the player, not from the header.
    if (this.hasPanelTarget && !this.panelTarget.contains(target)) return;
    if (target.closest("input, textarea, select, [contenteditable]")) return;
    const space = event.key === " ";
    if (!space && event.key.toLowerCase() !== "k") return;
    // A focused button handles Space itself.
    if (space && target.closest("button, a")) return;
    if (!this.hasPlayButtonTarget || this.playButtonTarget.disabled) return;
    event.preventDefault();
    this.toggle();
  }

  // --- rendering ------------------------------------------------------------

  measure() {
    if (!this.hasSliderTarget) return;
    const width = this.sliderTarget.clientWidth;
    if (width === this.width) return;
    this.width = width;
    this.drawBars();
    this.render();
  }

  drawBars() {
    const count = barCount(this.width);
    if (count === this.lastBars && count > 0) return;
    this.lastBars = count;
    const peaks = serverPeaks(this.peaksValue);
    const d = barsPath(barHeights(peaks, this.seedValue, count), HEIGHT);
    this.basePathTarget.setAttribute("d", d);
    this.playedPathTarget.setAttribute("d", d);
  }

  render() {
    if (!this.hasSliderTarget) return;
    const duration = this.duration;
    const position = clampPosition(this.position, duration);
    const fraction = duration > 0 ? Math.min(1, position / duration) : 0;

    this.playedTarget.style.clipPath = `inset(0 ${((1 - fraction) * 100).toFixed(3)}% 0 0)`;
    this.playheadTarget.style.left = `${(fraction * 100).toFixed(3)}%`;

    const whole = Math.floor(position);
    const key = `${whole}/${Math.floor(duration)}`;
    if (key === this.lastKey) return;
    this.lastKey = key;
    this.elapsedTarget.textContent = formatDuration(position);
    this.remainingTarget.textContent = formatRemaining(position, duration);
    const slider = this.sliderTarget;
    slider.setAttribute("aria-valuemax", String(Math.floor(duration)));
    slider.setAttribute("aria-valuenow", String(whole));
    slider.setAttribute(
      "aria-valuetext",
      `${formatDuration(position)} of ${formatDuration(duration)}`,
    );
  }

  renderButtons() {
    if (!this.hasPlayButtonTarget) return;
    const button = this.playButtonTarget;
    button.disabled = this.failed;
    button.setAttribute("aria-label", this.playing ? "Pause" : "Play");
    button.setAttribute("aria-busy", String(this.loading));
    this.skipBackTarget.disabled = this.failed;
    this.skipForwardTarget.disabled = this.failed;
    this.spinnerIconTarget.classList.toggle("hidden", !this.loading);
    this.pauseIconTarget.classList.toggle("hidden", this.loading || !this.playing);
    this.playIconTarget.classList.toggle("hidden", this.loading || this.playing);
  }
}
